import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CreditCard, Eye, CheckCircle, XCircle, FileText, Calendar, AlertCircle, Upload, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import InvoiceViewer from '@/components/InvoiceViewer';
import CurrencySymbol from '@/components/CurrencySymbol';
import { SearchFilter } from '@/components/SearchFilter';
import { formatCurrency, fmtNum } from '@/utils/currency';

export default function CreditInvoices() {
  const { token } = useContext(AuthContext);
  const [creditInvoices, setCreditInvoices] = useState([]);
  const [searchFilteredInvoices, setSearchFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentProofDialog, setShowPaymentProofDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyAction, setVerifyAction] = useState('verify'); // 'verify' or 'reject'
  const [rejectReason, setRejectReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'verifying', 'verified'

  // Helper to display price with proper currency symbol
  const PriceDisplay = ({ amount, currency = 'USD' }) => {
    const formattedAmount = fmtNum(amount || 0);
    
    if (currency === 'MVR') {
      return (
        <span>
          <CurrencySymbol currency="MVR" /> {formattedAmount}
        </span>
      );
    }
    
    const symbol = currency === 'CHN' ? '¥' : '$';
    return <span>{symbol}{formattedAmount}</span>;
  };
  
  // Payment verification flow states (similar to Invoices.jsx)
  const [paymentStep, setPaymentStep] = useState(1); // 1: Currency, 2: Bank, 3: Slip
  const [paymentData, setPaymentData] = useState({
    currency: '',
    bank_id: '',
    payment_method: 'transfer',
    payment_slip: null
  });
  const [paymentSlipPreview, setPaymentSlipPreview] = useState(null);
  const [banks, setBanks] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);

  useEffect(() => {
    fetchCreditInvoices();
    fetchBanks();
    fetchExchangeRates();
  }, []);

  const fetchCreditInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices/credit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditInvoices(response.data);
      setSearchFilteredInvoices(response.data);
    } catch (error) {
      console.error('Error fetching credit invoices:', error);
      toast.error('Failed to load credit invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/currency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExchangeRates(response.data.exchange_rates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates');
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API}/settings/invoice-quotation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data.payment_banks || []);
      
      // Get unique currencies from all banks
      const currencies = new Set();
      response.data.payment_banks?.forEach(bank => {
        bank.accounts?.forEach(account => {
          if (account.account_number) {
            currencies.add(account.currency);
          }
        });
      });
      setAvailableCurrencies(Array.from(currencies));
    } catch (error) {
      console.error('Failed to fetch banks');
    }
  };

  const getConvertedAmount = () => {
    if (!selectedInvoice || !paymentData.currency) return 0;
    
    // If currencies match, no conversion needed
    if (selectedInvoice.currency === paymentData.currency) {
      return selectedInvoice.total;
    }
    
    // Convert between different currencies
    if (selectedInvoice.currency === 'USD' && paymentData.currency === 'MVR') {
      // USD to MVR
      const rate = exchangeRates.find(r => r.currency === 'MVR');
      if (rate) {
        return selectedInvoice.total * rate.rate_to_usd;
      }
    } else if (selectedInvoice.currency === 'MVR' && paymentData.currency === 'USD') {
      // MVR to USD
      const rate = exchangeRates.find(r => r.currency === 'MVR');
      if (rate && rate.rate_to_usd > 0) {
        return selectedInvoice.total / rate.rate_to_usd;
      }
    }
    
    return selectedInvoice.total;
  };

  const handleViewInvoice = async (invoice) => {
    setViewingInvoice(invoice);
    setShowViewer(true);
  };

  const handleViewPaymentProof = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentProofDialog(true);
  };

  const startVerifyPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentStep(1);
    setPaymentData({
      currency: '',
      bank_id: '',
      payment_method: 'transfer',
      payment_slip: null
    });
    setPaymentSlipPreview(null);
    setVerifyAction('verify');
    setShowVerifyDialog(true);
  };

  const handlePaymentSlipUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPaymentData({ ...paymentData, payment_slip: reader.result });
      setPaymentSlipPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleVerifyPayment = async () => {
    if (!selectedInvoice) return;
    
    try {
      let amountInPaymentCurrency = selectedInvoice.total;
      
      // Only convert if invoice currency is different from payment currency
      if (selectedInvoice.currency !== paymentData.currency) {
        // Get exchange rates for conversion
        const currencyResponse = await axios.get(`${API}/settings/currency`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const exchangeRates = currencyResponse.data.exchange_rates || [];
        
        // Convert between currencies
        if (selectedInvoice.currency === 'USD' && paymentData.currency === 'MVR') {
          // USD to MVR
          const rate = exchangeRates.find(r => r.currency === 'MVR');
          if (rate) {
            amountInPaymentCurrency = selectedInvoice.total * rate.rate_to_usd;
          }
        } else if (selectedInvoice.currency === 'MVR' && paymentData.currency === 'USD') {
          // MVR to USD
          const rate = exchangeRates.find(r => r.currency === 'MVR');
          if (rate && rate.rate_to_usd > 0) {
            amountInPaymentCurrency = selectedInvoice.total / rate.rate_to_usd;
          }
        }
        // Add more currency conversions as needed
      }
      // If currencies match, no conversion needed - use invoice total as-is
      
      // Record payment with the correct amount
      await axios.post(`${API}/accounting/record-payment`, {
        reference_id: selectedInvoice.invoice_number,
        reference_type: 'invoice',
        payment_currency: paymentData.currency,
        payment_method: paymentData.payment_method,
        bank_id: paymentData.bank_id,
        amount: amountInPaymentCurrency,
        payment_slip: paymentData.payment_slip
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Verify payment and convert credit invoice to normal invoice
      await axios.post(
        `${API}/admin/invoices/${selectedInvoice.id}/verify-payment`,
        { verified: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payment recorded and credit invoice verified!');
      setShowVerifyDialog(false);
      setRejectReason('');
      fetchCreditInvoices();
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to process payment verification');
    }
  };

  const handleRejectPayment = async () => {
    if (!selectedInvoice || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/invoices/${selectedInvoice.id}/verify-payment`,
        { 
          verified: false,
          reason: rejectReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment rejected');
      setShowVerifyDialog(false);
      setRejectReason('');
      setVerifyAction('verify');
      fetchCreditInvoices();
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error('Failed to reject payment');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'destructive', label: 'Pending Payment', icon: AlertCircle },
      verifying: { variant: 'default', label: 'Verifying Payment', icon: FileText },
      verified: { variant: 'default', label: 'Payment Verified', icon: CheckCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const filteredInvoices = searchFilteredInvoices.filter(invoice => {
    if (filterStatus === 'all') return true;
    return invoice.payment_status === filterStatus;
  });

  const handleExportPDF = () => {
    const rows = [];
    creditInvoices.forEach((inv) => {
      (inv.items || []).forEach((item) => {
        rows.push([
          rows.length + 1,
          inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-',
          inv.invoice_number || '-',
          inv.customer_name || '-',
          item.product_name || item.description || '-',
          `$${fmtNum(item.price || 0)}`,
          item.quantity || 0,
          `$${fmtNum(item.total || 0)}`
        ]);
      });
    });
    const total = creditInvoices.reduce((s, inv) => s + (inv.total || 0), 0);
    exportListPDF({
      title: 'Credit Invoices',
      headers: ['#', 'Date', 'Invoice#', 'Customer', 'Item Name', 'Price', 'Qty', 'Total'],
      rows,
      totalLabel: 'Grand Total',
      totalValue: `$${fmtNum(total)}`,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Credit Invoices</h1>
              <p className="text-sm text-gray-600">Manage customer credit invoices and payment verification</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleExportPDF} data-testid="credit-invoices-export-pdf">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>

        {/* Search Filter */}
        <div className="mb-4">
          <SearchFilter
            data={creditInvoices}
            onFilter={setSearchFilteredInvoices}
            searchFields={['invoice_number', 'customer_name', 'customer_email']}
            placeholder="Search by invoice #, customer name..."
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('all')}
          >
            All ({creditInvoices.length})
          </Button>
          <Button
            variant={filterStatus === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('pending')}
          >
            Pending ({creditInvoices.filter(i => i.payment_status === 'pending').length})
          </Button>
          <Button
            variant={filterStatus === 'verifying' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('verifying')}
          >
            Verifying ({creditInvoices.filter(i => i.payment_status === 'verifying').length})
          </Button>
          <Button
            variant={filterStatus === 'verified' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('verified')}
          >
            Verified ({creditInvoices.filter(i => i.payment_status === 'verified').length})
          </Button>
        </div>

        {/* Credit Invoices List */}
        {loading ? (
          <div className="text-center py-8">Loading credit invoices...</div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="p-8 text-center">
            <CreditCard className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No credit invoices found</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredInvoices.map((invoice) => {
              const daysOverdue = getDaysOverdue(invoice.due_date);
              const isOverdue = daysOverdue > 0 && invoice.payment_status !== 'verified';

              return (
                <Card key={invoice.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-800">
                          Invoice #{invoice.invoice_number}
                        </h3>
                        {getStatusBadge(invoice.payment_status)}
                        {isOverdue && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {daysOverdue} days overdue
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Customer</p>
                          <p className="font-medium">{invoice.customer_name}</p>
                          <p className="text-sm text-gray-600">{invoice.customer_email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Amount</p>
                          <p className="text-2xl font-bold text-blue-600">
                            <PriceDisplay amount={invoice.total} currency={invoice.currency} />
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Terms</p>
                          <p className="font-medium">{invoice.terms || 'Net 30 Days'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Due Date</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>

                      {invoice.payment_proof_url && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800 font-medium mb-2">Payment Proof Uploaded</p>
                          <p className="text-xs text-gray-600">
                            Uploaded: {new Date(invoice.payment_proof_uploaded_at).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {invoice.payment_rejection_reason && (
                        <div className="mt-4 p-3 bg-red-50 rounded-lg">
                          <p className="text-sm text-red-800 font-medium mb-1">Payment Rejected</p>
                          <p className="text-xs text-gray-600">{invoice.payment_rejection_reason}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewInvoice(invoice)}
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Invoice
                      </Button>

                      {invoice.payment_proof_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewPaymentProof(invoice)}
                          className="flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" />
                          View Proof
                        </Button>
                      )}

                      {invoice.payment_status === 'verifying' && (
                        <>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => startVerifyPayment(invoice)}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Verify
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setVerifyAction('reject');
                              setPaymentStep(1);
                              setShowVerifyDialog(true);
                            }}
                            className="flex items-center gap-2"
                          >
                            <XCircle className="w-4 h-4" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Invoice Viewer */}
        {viewingInvoice && (
          <InvoiceViewer 
            invoice={viewingInvoice} 
            open={showViewer} 
            onClose={() => setShowViewer(false)} 
          />
        )}

        {/* Payment Proof Viewer Dialog */}
        {showPaymentProofDialog && selectedInvoice && (
          <Dialog open={showPaymentProofDialog} onOpenChange={setShowPaymentProofDialog}>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Payment Proof - Invoice #{selectedInvoice.invoice_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {selectedInvoice.payment_proof_url ? (
                  <div>
                    <img
                      src={selectedInvoice.payment_proof_url}
                      alt="Payment Proof"
                      className="w-full rounded-lg border"
                    />
                    <p className="text-sm text-gray-600 mt-2">
                      Uploaded: {new Date(selectedInvoice.payment_proof_uploaded_at).toLocaleString()}
                    </p>
                  </div>
                ) : (
                  <p className="text-center text-gray-600 py-8">No payment proof uploaded</p>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Verify/Reject Dialog */}
        {showVerifyDialog && selectedInvoice && (
          <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {verifyAction === 'verify' && paymentStep === 1 && 'Select Payment Currency'}
                  {verifyAction === 'verify' && paymentStep === 2 && 'Select Bank to Credit'}
                  {verifyAction === 'verify' && paymentStep === 3 && 'Upload Payment Slip'}
                  {verifyAction === 'reject' && 'Reject Payment'}
                </DialogTitle>
              </DialogHeader>

              {verifyAction === 'verify' && (
                <>
                  {/* Step 1: Currency Selection */}
                  {paymentStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Invoice #{selectedInvoice.invoice_number} - {selectedInvoice.customer_name}
                      </p>
                      <p className="text-sm text-slate-600">Choose the currency customer paid in:</p>
                      <div className="grid grid-cols-3 gap-3">
                        {availableCurrencies.map((currency) => (
                          <Button
                            key={currency}
                            variant={paymentData.currency === currency ? "default" : "outline"}
                            className={paymentData.currency === currency ? "bg-emerald-600" : ""}
                            onClick={() => {
                              setPaymentData({ ...paymentData, currency });
                              setPaymentStep(2);
                            }}
                          >
                            {currency}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Bank Selection */}
                  {paymentStep === 2 && (
                    <div className="space-y-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPaymentStep(1)}
                        className="mb-2"
                      >
                        ← Back
                      </Button>
                      <p className="text-sm text-slate-600">
                        Selected Currency: <strong>{paymentData.currency}</strong>
                      </p>
                      <p className="text-sm text-slate-600">Select bank to credit:</p>
                      <div className="grid grid-cols-2 gap-3">
                        {banks.filter(bank => 
                          bank.accounts.some(acc => acc.currency === paymentData.currency && acc.account_number)
                        ).map((bank) => (
                          <Button
                            key={bank.id}
                            variant="outline"
                            className={`h-auto py-4 ${paymentData.bank_id === bank.id ? 'border-emerald-600 bg-emerald-50' : ''}`}
                            onClick={() => {
                              setPaymentData({ ...paymentData, bank_id: bank.id });
                              setPaymentStep(3);
                            }}
                          >
                            {bank.bank_logo && (
                              <img 
                                src={bank.bank_logo} 
                                alt={bank.bank_name} 
                                className="h-12 w-auto object-contain"
                              />
                            )}
                            {!bank.bank_logo && <span>{bank.bank_name}</span>}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Payment Slip Upload */}
                  {paymentStep === 3 && (
                    <div className="space-y-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPaymentStep(2)}
                        className="mb-2"
                      >
                        ← Back
                      </Button>
                      <div className="text-sm text-slate-600 space-y-1">
                        <p>Payment Currency: <strong>{paymentData.currency}</strong></p>
                        <p>Invoice Amount: <strong className="inline-flex items-center">
                          <CurrencySymbol currency={selectedInvoice?.currency} />
                          {fmtNum(selectedInvoice?.total)} {selectedInvoice?.currency}
                        </strong></p>
                        {selectedInvoice?.currency !== paymentData.currency && (
                          <p className="text-emerald-600 font-semibold">
                            Converted Amount to Pay: <strong className="inline-flex items-center">
                              <CurrencySymbol currency={paymentData.currency} />
                              {fmtNum(getConvertedAmount())} {paymentData.currency}
                            </strong>
                          </p>
                        )}
                        {selectedInvoice?.currency === paymentData.currency && (
                          <p className="text-emerald-600 font-semibold">
                            ✓ Paying in same currency - No conversion needed
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <Label>Upload Payment Slip (Optional)</Label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePaymentSlipUpload}
                          className="hidden"
                          id="payment-slip-upload"
                        />
                        <label htmlFor="payment-slip-upload">
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => document.getElementById('payment-slip-upload').click()}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {paymentSlipPreview ? 'Change Slip' : 'Upload Slip'}
                          </Button>
                        </label>
                        {paymentSlipPreview && (
                          <div className="mt-2">
                            <img 
                              src={paymentSlipPreview} 
                              alt="Payment Slip" 
                              className="w-full h-32 object-cover rounded border"
                            />
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={handleVerifyPayment}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete Payment & Verify
                      </Button>
                    </div>
                  )}
                </>
              )}

              {verifyAction === 'reject' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Invoice #{selectedInvoice.invoice_number} - {selectedInvoice.customer_name}
                  </p>
                  <p className="text-lg font-semibold">
                    Amount: <PriceDisplay amount={selectedInvoice.total} currency={selectedInvoice.currency} />
                  </p>
                  <div>
                    <label className="text-sm font-medium">Rejection Reason *</label>
                    <textarea
                      className="w-full mt-1 p-2 border rounded-md"
                      rows="3"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                    />
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowVerifyDialog(false);
                        setRejectReason('');
                        setVerifyAction('verify');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRejectPayment}
                      variant="destructive"
                      disabled={!rejectReason.trim()}
                    >
                      Reject Payment
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
